import React, { useState, useEffect } from 'react';

const WhatsAppButton = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        // Show button after a delay when page loads
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 2000);

        // Show tooltip after another delay
        const tooltipTimer = setTimeout(() => {
            setShowTooltip(true);
            // Hide tooltip after 4 seconds
            setTimeout(() => setShowTooltip(false), 4000);
        }, 4000);

        // Scroll event to hide/show button and update progress
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
            const progress = Math.min(100, (scrollY / documentHeight) * 100);

            setScrollProgress(progress);
            setIsVisible(scrollY > 300);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            clearTimeout(timer);
            clearTimeout(tooltipTimer);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const openWhatsApp = () => {
        const message = "Hola, estoy interesado/a en obtener más información sobre los cursos de formación profesional de Escuela Verónica Dandrea. ¿Podrían asesorarme?";
        window.open(`https://wa.me/59894075255?text=${encodeURIComponent(message)}`, '_blank');
    };

    return (
        <>
            {/* Main WhatsApp Button */}
            <div
                className={`fixed bottom-6 right-6 z-50 transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-95'
                    }`}
            >
                <div className="relative">
                    {/* Professional Tooltip */}
                    <div
                        className={`absolute bottom-full right-0 mb-4 transition-all duration-300 transform ${showTooltip && !isHovered ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-2 opacity-0 scale-95'
                            }`}
                    >
                        <div className="bg-white rounded-2xl px-8 py-4 shadow-2xl border border-amber-200 whitespace-nowrap relative backdrop-blur-sm">
                            <p className="text-stone-700 font-medium text-sm flex items-center space-x-3">
                                <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.884 3.703" />
                                </svg>
                                <span>¿Necesitas asesoría?</span>
                                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                            </p>
                            <div className="absolute top-full right-8 w-0 h-0 border-l-4 border-r-4 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
                        </div>
                    </div>

                    {/* Main Professional Button */}
                    <button
                        onClick={openWhatsApp}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        className={`wpp-button group relative w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-full shadow-2xl hover:shadow-emerald-500/30 transition-all duration-300 transform hover:scale-110 flex items-center justify-center ${isHovered ? 'shadow-emerald-500/30 shadow-2xl' : 'shadow-xl'
                            }`}
                    >
                        {/* Professional Pulse Animation Rings */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 animate-ping opacity-20"></div>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 animate-pulse opacity-30"></div>

                        {/* WhatsApp Icon */}
                        <svg
                            className="w-8 h-8 text-white relative z-10 group-hover:scale-110 transition-transform duration-300"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.884 3.703" />
                        </svg>

                        {/* Professional Notification Dot */}
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
                            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                        </div>
                    </button>
                </div>
            </div>
            {/* Professional CSS for enhanced animations */}
            <style jsx>{`
                /* Professional glow effect for button */
                .wpp-button:hover .relative {
                    filter: drop-shadow(0 0 15px rgba(16, 185, 129, 0.4));
                }
            `}</style>
        </>
    );
};

export default WhatsAppButton; 